'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useTranslations } from 'next-intl';

import dayjs from 'dayjs';

import { ChatAttachment, ChatMessage, ChatStreamChunk, ToolCallAction, ToolCallChunk, ToolsRegistry } from '@repo/contracts';

import { useChatAPI } from '@hooks/chatbot/useChatAPI';
import { ChatMessageWithId, useChatMessages } from '@hooks/chatbot/useChatMessages';
import { useToolExecutor } from '@hooks/chatbot/useToolExecutor';
import { useHateoas } from '@hooks/hateoas/useHateoas';

import { useAIContext } from '@context/AiContext';

import { getPublicChatApiUrl } from '@utils/runtime-config';

const MAX_TOOL_ITERATIONS = 10;

function toApiMessages(msgs: ChatMessageWithId[]): ChatMessage[] {
    return msgs.map(({ _id: _, ...msg }) => msg as ChatMessage);
}

function filterToolsForChatbot(tools: ToolsRegistry): ToolsRegistry {
    const filtered: ToolsRegistry = {};
    for (const [scope, actions] of Object.entries(tools)) {
        const filteredActions: typeof actions = {};
        for (const [rel, target] of Object.entries(actions)) {
            if (target.method === 'DELETE' || target.chatbotHidden === true || !target.tool) {
                continue;
            }
            filteredActions[rel] = target;
        }
        if (Object.keys(filteredActions).length > 0) {
            filtered[scope] = filteredActions;
        }
    }
    return filtered;
}
 
export function useChatbot() {
    const t = useTranslations();
    const aiContext = useAIContext();
    const registry = aiContext?.memory ?? {};
    const tools = aiContext?.tools ?? {};
    const executeFrontAction = aiContext?.executeFrontAction;

    // Mise à jour du context entre les appels
    const latestRegistry = useRef(registry);
    useEffect(() => {
        latestRegistry.current = registry;
    }, [registry]);

    const { fetchLink } = useHateoas();
    const {
        conversations,
        activeId,
        activeMessages,
        input,
        setInput,
        createConversation,
        switchConversation,
        deleteConversation,
        restoreConversations,
        renameActiveConversation,
        renameConversation,
        appendMessage,
        appendMessages,
        resetMessages,
        sliceMessagesAtLastUser,
    } = useChatMessages();
    const { executeTool, executingToolLabel } = useToolExecutor({ fetchLink });
    const { sendStreamChatRequest, fetchQuota, fetchPublicSettings, generateTitle, fetchConversations, saveConversation, updateConversation, deleteConversationApi } = useChatAPI({ chatUrl: getPublicChatApiUrl() });

    const [isLoading, setIsLoading] = useState(false);
    const [isExecutingTool, setIsExecutingTool] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const [isApiAvailable, setIsApiAvailable] = useState(false);
    const [quota, setQuota] = useState<{ max: number; remaining: number | null } | null>(null);
    const [retentionDays, setRetentionDays] = useState<number>(14);
    const [streamingText, setStreamingText] = useState('');
    const [attachments, setAttachments] = useState<ChatAttachment[]>([]);
    const [generatingTitleId, setGeneratingTitleId] = useState<string | null>(null);
    const streamingTextRef = useRef('');
    const abortControllerRef = useRef<AbortController | null>(null);
    const pendingTitlePromptRef = useRef<{ prompt: string; id: string } | null>(null);

    const groupedConversations = useMemo(() => {
        const today: typeof conversations = [];
        const yesterday: typeof conversations = [];
        const last7Days: typeof conversations = [];
        const older: typeof conversations = [];
        const now = dayjs();

        conversations
            .filter((c) => c.messages.length > 0)
            .sort((a, b) => b.updatedAt - a.updatedAt)
            .forEach((conv) => {
                const date = dayjs(conv.updatedAt);
                if (date.isSame(now, 'day')) {
                    today.push(conv);
                } else if (date.isSame(now.subtract(1, 'day'), 'day')) {
                    yesterday.push(conv);
                } else if (date.isAfter(now.subtract(7, 'day'))) {
                    last7Days.push(conv);
                } else {
                    older.push(conv);
                }
            });

        return [
            { label: t('today'), items: today },
            { label: t('yesterday'), items: yesterday },
            { label: t('last-nb-days', { nb: 7 }), items: last7Days },
            { label: t('older'), items: older },
        ].filter((group) => group.items.length > 0);
    }, [conversations, t]);

    // Restauration de la conversation et chargement du quota au montage
    useEffect(() => {
        const init = async () => {
            try {
                const fetchedConversations = await fetchConversations();
                let found = false;
                if (Array.isArray(fetchedConversations) && fetchedConversations.length > 0) {
                    restoreConversations(fetchedConversations, null);
                    const lastActiveId = localStorage.getItem('chatbot_active_conversation_id');
                    if (lastActiveId && fetchedConversations.some((c: any) => c.id === lastActiveId)) {
                        switchConversation(lastActiveId);
                        found = true;
                    }
                }
                
                if (!found) {
                    createConversation();
                }
            } catch (err) {
                console.error('Erreur lors de la lecture de l\'historique du chat depuis l\'API:', err);
                createConversation();
            }

            try {
                const quotaData = await fetchQuota();
                setQuota(quotaData);
                setIsApiAvailable(true);
            } catch {
                // L'API chatbot-api n'est pas disponible (ex: 404, ECONNREFUSED)
                setIsApiAvailable(false);
            }

            try {
                const publicSettings = await fetchPublicSettings();
                setRetentionDays(publicSettings.conversationRetentionDays);
            } catch (err) {
                console.error('Erreur lors de la lecture des paramètres publics du chat:', err);
            }

            setIsInitialized(true);
        };
        init();
    }, [fetchQuota, fetchConversations, restoreConversations, createConversation, switchConversation, fetchPublicSettings]);

    // Sauvegarde de la conversation active à chaque modification
    const activeConversation = conversations.find((c) => c.id === activeId);
    
    // On garde une trace de l'ID précédent pour ne sauvegarder que s'il s'agit d'une mutation locale
    // de la conversation en cours, et non d'un simple changement d'onglet (changement de activeId).
    const prevActiveIdRef = useRef(activeId);
    useEffect(() => {
        const isTabSwitch = prevActiveIdRef.current !== activeId;
        prevActiveIdRef.current = activeId;

        if (isInitialized && activeConversation && !isTabSwitch) {
            // Uniquement sauvegarder si la conversation a des messages ou a été modifiée
            if (activeConversation.messages.length > 0 || activeConversation.title !== t('new')) {
                // On retire la data en base64 très lourde pour éviter d'alourdir la BDD inutilement
                const safeMessages = activeConversation.messages.map((msg) => ({
                    ...msg,
                    attachments: msg.attachments?.map((att) => ({ ...att, data: '' })),
                }));
                saveConversation({
                    id: activeConversation.id,
                    title: activeConversation.title,
                    messages: toApiMessages(safeMessages as any),
                }).catch((err) => console.error('Erreur lors de la sauvegarde de la conversation:', err));
            }
        }
    }, [activeConversation, activeId, isInitialized, saveConversation, t]);

    // Génération du titre une fois le premier échange terminé (ou annulé)
    useEffect(() => {
        if (!isLoading && isInitialized && pendingTitlePromptRef.current) {
            const { prompt, id } = pendingTitlePromptRef.current;
            pendingTitlePromptRef.current = null; // Clear it to avoid infinite loops

            const targetConv = conversations.find((c) => c.id === id);
            const lastMessage = targetConv?.messages[targetConv.messages.length - 1];
            const assistantResponse = (lastMessage && lastMessage.role === 'assistant') ? lastMessage.content : undefined;

            setGeneratingTitleId(id);

            generateTitle(prompt, assistantResponse).then((title) => {
                renameConversation(id, title);
                updateConversation(id, { title }).catch((err) => console.error('Erreur de save du titre:', err));
            }).catch((err) => {
                console.error('Failed to generate chat title:', err);
                let fallbackTitle = prompt;
                if (fallbackTitle.length > 15) {
                    fallbackTitle = fallbackTitle.substring(0, 15) + '...';
                }
                renameConversation(id, fallbackTitle);
                updateConversation(id, { title: fallbackTitle }).catch((err) => console.error('Erreur de save du titre:', err));
            }).finally(() => {
                setGeneratingTitleId(null);
            });
        }
    }, [isLoading, isInitialized, conversations, generateTitle, renameConversation]);

    const handleDeleteConversation = useCallback(async (id: string) => {
        deleteConversation(id);
        try {
            await deleteConversationApi(id);
        } catch (err) {
            console.error('Erreur lors de la suppression de la conversation:', err);
        }
    }, [deleteConversation, deleteConversationApi]);

    // Force l'arrêt de la requête
    const stop = useCallback(() => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
        setIsLoading(false);
        setIsExecutingTool(false);
        
        if (streamingTextRef.current) {
            appendMessage({ role: 'assistant', content: streamingTextRef.current });
            streamingTextRef.current = '';
            setStreamingText('');
        }
    }, [appendMessage]);

    // Soumettre le message modifié
    const submitEditedMessage = async (newContent: string) => {
        if (isLoading) stop(); 
        const previousMessages = sliceMessagesAtLastUser();

        const userMessage: ChatMessage = { role: 'user', content: newContent };
        const apiHistory = [...toApiMessages(previousMessages), userMessage];

        appendMessage(userMessage);
        setIsLoading(true);

        await agentLoop(apiHistory, 0);
    };

    // Vider explicitement la conversation courante
    const clearChat = () => {
        resetMessages();
        streamingTextRef.current = '';
        setStreamingText('');
    };

    // Soumission du message utilisateur
    const handleSubmit = async (e?: React.FormEvent) => {
        if (e) e.preventDefault();
        if ((!input.trim() && attachments.length === 0) || isLoading) return;

        const textToSend = input;
        const attachmentsToSend = attachments.length > 0 ? [...attachments] : undefined;
        setInput('');
        setAttachments([]);
        
        await sendMessage(textToSend, attachmentsToSend);
    };

    // Envoyer un message directement à l'API
    const sendMessage = async (content: string, attachmentsList?: ChatAttachment[]) => {
        if (isLoading) return;

        // Met à jour le titre de la conversation s'il s'agit du premier message
        if (activeMessages.length === 0) {
            renameActiveConversation('');
            
            let promptText = content.trim();
            if (!promptText && attachmentsList && attachmentsList[0]) {
                promptText = 'Document: ' + attachmentsList[0].name;
            } else if (promptText.startsWith('[ACTION:')) {
                const actionMatch = promptText.match(/\\[ACTION:(.+?)\\]/);
                if (actionMatch && actionMatch[1]) {
                    promptText = actionMatch[1];
                }
            }

            // On stocke le prompt pour générer le titre à la fin du 1er échange
            if (activeId) {
                pendingTitlePromptRef.current = { prompt: promptText, id: activeId };
                setGeneratingTitleId(activeId);
            }
        }

        const userMessage: ChatMessage = { 
            role: 'user', 
            content, 
            attachments: attachmentsList && attachmentsList.length > 0 ? attachmentsList : undefined, 
        };
        const apiHistory = [...toApiMessages(activeMessages), userMessage];

        appendMessage(userMessage);
        setIsLoading(true);

        await agentLoop(apiHistory, 0);
    };

    const agentLoop = async (history: ChatMessage[], depth: number) => {
        if (depth > MAX_TOOL_ITERATIONS) {
            appendMessage({
                role: 'assistant',
                content: t('chatbot-error-action-limit'),
            });
            setIsLoading(false);
            setIsExecutingTool(false);
            return;
        }

        streamingTextRef.current = '';
        setStreamingText('');
        let pendingToolCallChunk: ToolCallChunk | null = null;

        try {
            abortControllerRef.current = new AbortController();

            await sendStreamChatRequest(
                activeId ?? '',
                history,
                latestRegistry.current,
                filterToolsForChatbot(aiContext?.getTools?.() ?? tools),
                (chunk) => onChunk(chunk, (tc) => { pendingToolCallChunk = tc; }),
                abortControllerRef.current.signal,
            );

            // Décrémenter le quota uniquement lors du premier appel (pas lors des tool_calls)
            if (depth === 0) {
                setQuota((prev) => {
                    if (prev && prev.remaining !== null && prev.remaining > 0) {
                        return { ...prev, remaining: prev.remaining - 1 };
                    }
                    return prev;
                });
            }

            // Analyse de la décision de l'IA
            if (pendingToolCallChunk) {
                setIsExecutingTool(true);
                const toolMessages = await executeAllTools((pendingToolCallChunk as ToolCallChunk).actions);
                setIsExecutingTool(false);
                appendMessages(toolMessages);

                await new Promise((resolve) => setTimeout(resolve, 50));

                const updatedHistory = [...history, (pendingToolCallChunk as ToolCallChunk).assistantMessage, ...toolMessages];
                await agentLoop(updatedHistory, depth + 1);

            } else if (streamingTextRef.current.trim()) {
                appendMessage({ role: 'assistant', content: streamingTextRef.current.trim() });
                streamingTextRef.current = '';
                setStreamingText('');
                setIsLoading(false);
                setIsExecutingTool(false);
            } else {
                if (depth > 0) {
                    appendMessage({ role: 'assistant', content: t('chatbot-action-success') });
                }
                setIsLoading(false);
                setIsExecutingTool(false);
            }
        } catch (error: unknown) {
            if (error instanceof Error && error.message === 'QuotaExceeded') {
                appendMessage({ role: 'assistant', content: t('quota-exceeded') });
                setQuota((prev) => (prev ? { ...prev, remaining: 0 } : prev));
            } else {
                appendMessage({ role: 'assistant', content: t('chatbot-error-communication') });
            }
            setIsLoading(false);
            setIsExecutingTool(false);
        }
    };

    /**
     * Appelé pour chaque chunk SSE reçu du chat-api.
     * Trois cas possibles.
     */
    const onChunk = (chunk: ChatStreamChunk, setPendingToolCallChunk: (tc: ToolCallChunk) => void) => {
        if (chunk.type === 'content') {
            // Accumulation dans le ref ET dans le state
            streamingTextRef.current += chunk.content;
            setStreamingText(streamingTextRef.current);

        } else if (chunk.type === 'tool_calls') {
            // Le LLM a demandé des outils. On stocke le chunk pour onComplete
            // et on ajoute immédiatement le message assistant dans l'historique.
            setPendingToolCallChunk(chunk);
            streamingTextRef.current = '';
            setStreamingText('');
            appendMessage(chunk.assistantMessage as ChatMessage);

        } else if (chunk.type === 'text') {
            // Message d'erreur
            if (streamingTextRef.current === '') {
                streamingTextRef.current = chunk.content;
                setStreamingText(streamingTextRef.current);
            }
        } else if (chunk.type === 'tool_result') {
            appendMessage({
                role: 'tool',
                name: chunk.name,
                content: chunk.result,
                toolCallId: chunk.toolCallId,
            });
        } else if (chunk.type === 'error') {
            // Erreur critique de l'API (ex: timeout, 500)
            appendMessage({ role: 'assistant', content: chunk.message });
            setIsLoading(false);
            setIsExecutingTool(false);
        }
    };

    /**
     * Exécute toutes les actions en parallèle et retourne les messages
     * de résultat au format attendu par le LLM (role: 'tool').
     */
    const executeAllTools = async (actions: ToolCallAction[]): Promise<ChatMessage[]> => {
        const results = await Promise.all(
            actions.map(async (action) => {
                if (executeFrontAction) {
                    const front = await executeFrontAction(action.name, action.args ?? {});
                    if (front.handled) {
                        return {
                            role: 'tool' as const,
                            name: action.name,
                            content: JSON.stringify(front.error ? { status: 'Error', result: front.error } : { status: 'Success', result: t('chatbot-action-success') }),
                            toolCallId: action.id,
                        };
                    }
                }
                
                try {
                    const currentTools = aiContext?.getTools?.() ?? tools;
                    const result = await executeTool(action.name, action.args ?? {}, currentTools);

                    let cleanResult = result.result ?? null;

                    if (cleanResult) {
                        try {
                            const parsed = typeof cleanResult === 'string' ? JSON.parse(cleanResult) : cleanResult;
                            const newItems: Record<string, any> = {};
                            
                            if (Array.isArray(parsed)) {
                                parsed.forEach((item: any) => {
                                    if (item?.id || item?._id) newItems[item.id || item._id] = item;
                                });
                            } else if (parsed && typeof parsed === 'object') {
                                if (parsed.id || parsed._id) newItems[parsed.id || parsed._id] = parsed;
                                Object.values(parsed).forEach((val: any) => {
                                    if (Array.isArray(val)) {
                                        val.forEach((item: any) => {
                                            if (item?.id || item?._id) newItems[item.id || item._id] = item;
                                        });
                                    }
                                });
                            }
                        } catch {
                            // Ignorer les erreurs de parsing pour le dataById
                        }
                    }

                    if (cleanResult && cleanResult.length > 15000) {
                        try {
                            const parsed = JSON.parse(cleanResult);
                            if (Array.isArray(parsed) && parsed.length > 5) {
                                cleanResult = JSON.stringify(parsed.slice(0, 5)) + '\n\n[... Reste des données tronqué pour économiser le contexte IA ...]';
                            } else {
                                cleanResult = cleanResult.substring(0, 15000) + '\n\n[... Données trop longues, tronquées par le système ...]';
                            }
                        } catch {
                            cleanResult = cleanResult.substring(0, 15000) + '\n\n[... Données trop longues, tronquées ...]';
                        }
                    }
                    
                    return {
                        role: 'tool' as const,
                        name: action.name,
                        content: JSON.stringify({
                            status: result.success ? 'Success' : 'Error',
                            result: cleanResult,
                        }),
                        toolCallId: action.id,
                    };
                } catch (err: unknown) {
                    const message = err instanceof Error ? err.message : t('chatbot-tool-unexpected-error');
                    return {
                        role: 'tool' as const,
                        name: action.name,
                        content: JSON.stringify({
                            status: 'Error',
                            result: message,
                        }),
                        toolCallId: action.id,
                    };
                }
            }),
        );

        return results;
    };

    return {
        groupedConversations,
        activeId,
        messages: activeMessages,
        input,
        setInput,
        attachments,
        setAttachments,
        createConversation,
        switchConversation,
        deleteConversation: handleDeleteConversation,
        isLoading,
        isExecutingTool,
        executingToolLabel,
        handleSubmit,
        sendMessage,
        streamingText,
        clearChat,
        stop,
        submitEditedMessage,
        quota,
        retentionDays,
        isApiAvailable,
        generatingTitleId,
    };
}
